/**
 * Music Upload Store
 * Handles file uploads, analysis, transposition, and conversion to Griffschrift
 *
 * **History-Capture (explizit statt Flag+Watcher).** Jede erfolgreiche Operation legt ein
 * typisiertes {@link HistoryCaptureEvent} (Absicht + Daten **atomar**) in die
 * {@link captureQueue}; {@link @/composables/useHistoryCapture} konsumiert die Queue sequentiell
 * und reicht die Events an den File-History-Store weiter. Das frühere Seiten-Kanal-Flag
 * `lastUploadKind` (Intent-Rekonstruktion über fünf unabhängige Watcher) ist damit entfallen –
 * ein neuer Codepfad, der die Erfassung vergisst, verbucht **nichts** (statt etwas Falsches).
 *
 * **Abbrechbare Requests.** Pro Aktions-Typ hält der Store einen AbortController; ein neuer
 * Aufruf derselben Aktion bricht den Vorgänger ab (Stale-Response-Schutz). Abgebrochene
 * Requests erzeugen keine Fehler-Toasts ({@link isAbortError}).
 */

import {
    type AnalysisResult,
    type ChordInferenceSettings,
    ConversionMode,
    type ConversionSettings,
    type ConversionStatusStore,
    type DiatonicShiftSettings,
    type FileUploadResponse,
    type TranspositionResult,
    type VoiceMergeSettings,
} from '@/generated';
import {apiClient, extractApiErrorMessage, isAbortError} from '@/api-client';
import {
    applyGripToNoteInMusicXml,
    bassPreviewStatus,
    chordInferenceStatus,
    readBassPreviewStyles,
    setBassLyricInMusicXml,
    hasAnyChordSymbols,
    type BassPreviewState,
    type ChordInferenceStatus,
} from '@/composables/musicXmlDom';
import {useWorkingDocUrlState} from '@/composables/useWorkingDocUrlState';
import type {Grip} from '@/components/harmonika/gripTypes';
import type {NoteInfo} from '@/composables/musicXmlDom';
import {useNotificationStore} from '@/stores/notifications.ts';
import {useSettingsStore} from '@/stores/settings.ts';
import {pinConversionSettings} from '@/stores/settingsPins';
import {defineStore} from 'pinia';
import {computed, ref, type Ref} from 'vue';
import {
    createFileInfo, newLineageId,
    type HistoryCaptureEvent,
    isPdfFile,
    type PdfMovementDescriptor,
    type UploadIntent,
    validateFile
} from "@/stores/file.util.ts";
import {createRequestRegistry} from "@/shared/requestAbort";


export const useMusicUploadStore = defineStore('upload', () => {
    const notificationStore = useNotificationStore();
    const settingsStore = useSettingsStore();

    // ============ URL-basierter State (pro Tab isoliert, kein localStorage) ============
    // Aktuelle Server-file_id und Transposition leben in der URL (?file=...&semitones=...),
    // damit zwei Tabs konfliktfrei parallel arbeiten. Dauerhafte Persistenz übernimmt die
    // Dateihistorie (IndexedDB, useFileHistoryStore).
    // URL-gestützter Arbeitsdokument-Zustand (pro Tab konfliktfrei, überlebt Reload). Ausgelagert
    // nach @/composables/useWorkingDocUrlState – dort die einzige Quelle der ?file/?semitones/
    // ?lineage/?entry/?document/?reuse-Parameter. Dauerhafte Persistenz: Dateihistorie (IndexedDB).
    const {
        currentFileId,
        transposedSemitones,
        lineageId,
        currentEntryId,
        activeDocumentId,
        takeReuseId,
    } = useWorkingDocUrlState();

    // ============ Reactive State (Laufzeit, nicht persistiert) ============

    // File state. `fileInfo` hält nur die DATEI-METADATEN (kein `music_xml` – siehe toFileMeta):
    // kanonischer Inhalt ist allein `selectedFileContent` (Eingabe) bzw.
    // `conversionResult.music_xml` (Ausgabe).
    const fileInfo = ref<FileUploadResponse | null>(null);
    const selectedFileContent = ref<string | null>(null);
    // Exportiert für die Download-Buttons der Panels (DiatonicShiftPanel/VoiceMergePanel).
    const shiftResponse = ref<FileUploadResponse | null>(null);
    const mergeResponse = ref<FileUploadResponse | null>(null);

    /** Reduziert eine Server-Response auf die Datei-Metadaten für `fileInfo`. Ohne das Stripping
     *  hielte der Store denselben MusicXML-String mehrfach (die früheren Kopien `uploadResponse`/
     *  `transposeResponse` sind entfallen; deren einzige Leser waren ein nie greifender
     *  Content-Fallback und der Tab-Wechsel-Watcher, der jetzt `selectedFileContent` beobachtet). */
    function toFileMeta(response: FileUploadResponse): FileUploadResponse {
        return {...response, music_xml: null, analysis: null};
    }

    // Signal für den einmaligen Hinweis-Dialog bei mehrteiligen PDFs (Anzahl der Teile; null =
    // kein Hinweis). Wird von ``uploadAndAnalyzePdf`` gesetzt und vom Dialog per
    // ``dismissMultiPartNotice`` geleert – damit der Nutzer die Tab-Leiste nicht übersieht.
    const multiPartNotice = ref<number | null>(null);

    // Loading states
    const isUploading = ref(false);
    const isAnalyzing = ref(false);
    const isConverting = ref(false);
    const isTransposing = ref(false);
    const isShifting = ref(false);
    const isMerging = ref(false);
    const isBuildingBassPreview = ref(false);
    const isDetectingChords = ref(false);
    const isFindingTranspositions = ref(false);


    // PDF-Vorschau während Audiveris-Konvertierung
    const currentPdfObjectUrl = ref<string | null>(null);

    // Analysis & Conversion state
    const analysisResult = ref<AnalysisResult | null>(null);
    const transpositionSuggestions = ref<TranspositionResult[]>([]);
    // Variantwechsel-Vorschläge (Dur↔Moll, mode_change='parallel') – eigene Liste, weil
    // `semitones` nur je Liste eindeutig ist (Frontend-`:key`, siehe TranspositionPanel).
    const modeChangeSuggestions = ref<TranspositionResult[]>([]);
    const transpositionSuggestionsError = ref<string | null>(null);
    const conversionResult = ref<ConversionStatusStore | null>(null);
    // Der Eingabe-String, aus dem `conversionResult` entstand – Grundlage von
    // `isConversionStale`. Geschrieben ausschließlich von `setConversionResult`; nur zusammen mit
    // einem Ergebnis bedeutungsvoll, deshalb braucht er sonst nirgends Aufräumarbeit.
    const lastConvertedContent = ref<string | null>(null);

    // ============ History-Capture-Queue (explizit, atomar) ============

    // Queue statt Einzel-Ref: mehrere synchron aufeinanderfolgende Events dürfen sich nicht
    // gegenseitig überschreiben, bevor der Konsument (useHistoryCapture) sie verarbeitet hat.
    const captureQueue = ref<HistoryCaptureEvent[]>([]);

    function emitCapture(event: HistoryCaptureEvent): void {
        captureQueue.value = [...captureQueue.value, event];
    }

    /** Entnimmt alle anstehenden Capture-Events (Konsument: useHistoryCapture). */
    function drainCaptureQueue(): HistoryCaptureEvent[] {
        const events = captureQueue.value;
        captureQueue.value = [];
        return events;
    }

    // ============ Abbrechbare Requests (Stale-Response-Schutz) ============

    const requests = createRequestRegistry();
    const beginRequest = requests.begin;
    let transpositionRequestGeneration = 0;

    function resetTranspositionSuggestions(): void {
        transpositionRequestGeneration += 1;
        requests.abort('transposition-suggestions');
        transpositionSuggestions.value = [];
        modeChangeSuggestions.value = [];
        transpositionSuggestionsError.value = null;
        isFindingTranspositions.value = false;
    }

    // ============ Computed Properties ============

    const isHavingFile = computed(() => !!fileInfo.value);

    const isPending = computed(() =>
        isDetectingChords.value || isUploading.value || isAnalyzing.value || isConverting.value || isTransposing.value || isShifting.value || isMerging.value || isBuildingBassPreview.value
    );
    const _isMappable = computed(() => {
        const analysis = analysisResult.value;
        if (!analysis) return false;
        return (analysis.num_notes ?? 0) > (analysis.unmappable_occurrences_count ?? 0);
    });
    const isReadyToConvert = computed(() =>
        isHavingFile.value && _isMappable.value && !isPending.value)

    const isHavingGriffschriftResult = computed(() => !!conversionResult.value);

    // Passt die angezeigte Griffschrift noch zur aktuellen Eingabe? Konvertiert wird
    // ausschließlich auf Klick (kein Input-Schritt konvertiert nach) – eine vorhandene Ausgabe
    // bleibt danach stehen und wird stattdessen sichtbar als veraltet markiert (Griffschrift-Tab
    // + Konvertieren-Button). INHALTSBASIERT wie `chordInferenceState`/`bassPreviewState`: der
    // Vergleich läuft gegen den Inhalt, aus dem die Ausgabe wirklich entstand, nicht gegen ein
    // Flag, das jeder neue Schreibpfad setzen müsste. Im unveränderten Fall ist es ein
    // Referenzvergleich (dieselbe String-Instanz – Muster der Cache-Generation in
    // `composables/soundingNoteCache.ts`). AUSGABE-Edits (Griff, Bass-Silbe, Gesangstext der
    // Griffschrift) ändern `selectedFileContent` nicht und machen die Ausgabe daher zu Recht
    // nicht veraltet. Test: `__tests__/noAutoConvert.spec.ts`.
    const isConversionStale = computed(() =>
        !!conversionResult.value
        && lastConvertedContent.value !== (selectedFileContent.value ?? ''),
    );

    const isInputNotMappable = computed(() =>
        isHavingFile.value && analysisResult.value?.unmappable_occurrences_count && !isAnalyzing.value
    );

    // Inhaltsbasierter Ground-Truth-Status der automatischen Akkorderkennung für den aktuellen
    // Arbeitsinhalt: Anzahl der Auto-Akkorde (INFERRED_CHORD_COLOR-Marker) plus – aus den
    // Score-Metadaten – das nicht ableitbare Faktum, OB eine Erkennung gelaufen ist. Reaktiv
    // korrekt, weil applyChordInference/Upload/Transpose/Shift/Merge selectedFileContent setzen.
    // oldValue-Pattern (Pflicht für objekt-liefernde Computeds, siehe webapp/src/VUE_RULES.md):
    // identische Zustände geben dieselbe Referenz zurück, damit Watcher nicht bei jedem
    // Inhalts-Update feuern.
    const chordInferenceState = computed<ChordInferenceStatus>((oldValue) => {
        const next = chordInferenceStatus(selectedFileContent.value ?? '');
        if (oldValue?.state === next.state && oldValue.count === next.count) return oldValue;
        return next;
    });

    // Zustand des klingenden Bass-Vorschau-Systems, ebenfalls AUS DEM INHALT abgeleitet – damit
    // er Reload, Undo und Quelltext-Edits überlebt (ein Flag im Store täte das nicht). Primitiv,
    // deshalb ohne oldValue-Pattern.
    const bassPreviewState = computed<BassPreviewState>(
        () => bassPreviewStatus(selectedFileContent.value ?? ''),
    );

    // Enthält der aktuelle Arbeitsinhalt IRGENDEIN Akkordsymbol (eigenes oder auto-erkanntes)?
    // Steuert das Ausblenden des kompakten Erkennungs-Panels in der Vorschau. Inhaltsbasiert und
    // damit reaktiv/sofort – anders als das ggf. bei Live-Edits veraltete analysisResult.
    const hasChordSymbols = computed(() => hasAnyChordSymbols(selectedFileContent.value ?? ''));

    // ============ Upload Actions ============

    async function uploadFile(file: File, opts: {
        silent?: boolean;
        /** Bestimmt die History-Verbuchung; 'restore' erzeugt bewusst keinen Verlaufseintrag. */
        intent?: UploadIntent;
        /**
         * Gate + Settings der Upload-Akkorderkennung. Nur der **frische** Upload setzt es (und
         * nur bei aktivierter Präferenz); Re-Uploads (edit/shift/merge/restore) tragen bereits
         * finalen Inhalt und lassen das Feld weg → der Server erkennt nichts und lässt einen
         * vorhandenen Erkennungs-Vermerk unangetastet.
         */
        chordInference?: ChordInferenceSettings;
    } = {}): Promise<FileUploadResponse | null> {
        const intent = opts.intent ?? 'fresh';
        const info = createFileInfo(file);
        fileInfo.value = info;

        const validationError = validateFile(info);
        if (validationError) {
            notificationStore.notify(validationError, 'error');
            return null;
        }

        isUploading.value = true;

        try {
            // Hinweis: PDFs werden NICHT über ``uploadFile`` geladen, sondern über
            // ``uploadAndAnalyzePdf`` (mehrteilige PDFs → Movement-Dokument). Hier laufen nur
            // MusicXML/MIDI-Uploads (frisch, Edit, Shift, Merge, Restore, Reuse).
            // Das Bass-Layout zählt nur für die Erkennung (`restrict_to_bass_layout`).
            const response = await apiClient.uploadFile(
                file, opts.chordInference,
                {signal: beginRequest('upload')},
                opts.chordInference ? settingsStore.bassLayout : undefined,
            );

            // Update state
            currentFileId.value = response.file_id;
            fileInfo.value = toFileMeta(response);

            if (response.music_xml) {
                selectedFileContent.value = response.music_xml;
            }

            // History-Capture: Absicht + Daten atomar als Event verbuchen.
            if (intent === 'fresh') {
                emitCapture({
                    kind: 'upload',
                    lineageId: lineageId.value,
                    file,
                    filename: file.name || response.filename || 'datei.musicxml',
                    fileType: response.file_type ?? null,
                });
            } else if (intent === 'edit' || intent === 'shift' || intent === 'merge') {
                const musicXml = response.music_xml ?? selectedFileContent.value;
                if (musicXml) {
                    emitCapture({
                        kind: intent === 'edit' ? 'edited' : intent === 'shift' ? 'shifted' : 'merged',
                        lineageId: lineageId.value,
                        filename: response.filename || file.name || 'bearbeitet.musicxml',
                        musicXml,
                        semitones: transposedSemitones.value,
                        fileType: response.file_type ?? null,
                    });
                }
            }
            // intent === 'restore': bewusst kein Event (nur ein vorhandener Stand wird neu geladen).

            if (!opts.silent) notificationStore.notify('Datei erfolgreich hochgeladen.', 'success');
            return response;

        } catch (error) {
            if (isAbortError(error)) return null;
            notificationStore.notify(extractApiErrorMessage(error, 'Upload fehlgeschlagen'), 'error');
            return null;
        } finally {
            isUploading.value = false;
            if (currentPdfObjectUrl.value) {
                URL.revokeObjectURL(currentPdfObjectUrl.value);
                currentPdfObjectUrl.value = null;
            }
        }
    }

    async function updateAndAnalyzeFile(file: File, improveImage = false) {
        if (isPending.value) {
            notificationStore.notify(
                'Eine Analyse oder Konvertierung läuft bereits. Bitte warten.',
                'warning'
            );
            return {upload: null, analyze: null};
        }

        // Auto-Akkorderkennung nur, wenn der Nutzer sie im „Akkorderkennung"-Setting aktiviert hat
        // (opt-in; Default aus). Das `chord_inference`-Feld ist serverseitig zugleich das GATE:
        // weggelassen = keine Erkennung. Deshalb nur bei aktiver Präferenz mitschicken – sonst
        // bekäme eine frische Datei fälschlich einen Erkennungs-Vermerk. `overwrite_existing`
        // wird fix auf false gepinnt: ein Upload überschreibt nie Original-Akkorde (das Backend
        // honoriert alle gesendeten Werte; der Schutz ist Frontend-Sache).
        const chordInference = settingsStore.chordInference.enabled
            ? {...settingsStore.chordInference, overwrite_existing: false}
            : undefined;
        // Bass-Teil entfernen: persistierte UI-Präferenz (Default aus = Bass bleibt erhalten).
        // Wirkt beim Import nur für PDFs (der MusicXML-Upload behält per Invariante ohnehin alle Parts).
        const removeBass = settingsStore.removeBassParts ?? false;

        // PDFs laufen über den OMR-/Movement-Pfad (mehrteilige Stücke → Movement-Dokument).
        if (isPdfFile(file)) {
            return uploadAndAnalyzePdf(file, improveImage, removeBass, chordInference);
        }

        // Frischer Upload → neue Bearbeitungslinie, Transposition zurücksetzen, Dokument leeren.
        lineageId.value = newLineageId();
        transposedSemitones.value = 0;
        // Neue Kette: der Zeiger wird von der Verlaufs-Erfassung auf den neuen Root gesetzt.
        currentEntryId.value = null;
        activeDocumentId.value = null;

        const upload = await uploadFile(file, {intent: 'fresh', chordInference});
        if (!upload) {
            return {upload: null, analyze: null};
        }

        const analyzeResult = await analyze(upload);

        return {upload, analyze: analyzeResult};
    }

    /**
     * Lädt ein PDF via OMR hoch. Mehrteilige PDFs liefern **mehrere Movements**; jedes Movement
     * wird eine eigene Arbeitsdatei/Lineage, gruppiert über eine gemeinsame ``documentId``. Das
     * **erste** Movement wird zur aktiven Arbeitsbasis; die übrigen werden über das
     * ``document``-Capture-Event als eigene Verlaufseinträge erfasst und lassen sich per
     * Tab-Leiste (``switchMovement`` im History-Store) aktivieren. Einteilige PDFs verhalten sich
     * wie bisher (ein Upload, keine Tabs).
     */
    async function uploadAndAnalyzePdf(
        file: File, improveImage = false, removeBass = false,
        chordInference?: ChordInferenceSettings,
    ) {
        fileInfo.value = createFileInfo(file);
        isUploading.value = true;
        currentPdfObjectUrl.value = URL.createObjectURL(file);
        notificationStore.notify(
            'PDF wird mit Audiveris konvertiert... Dies kann 30-120 Sekunden dauern.',
            'info',
            5000
        );

        let movements: FileUploadResponse[] = [];
        let documentId = '';
        try {
            const resp = await apiClient.uploadPdf(
                file, improveImage, removeBass, chordInference,
                {signal: beginRequest('upload')},
                chordInference ? settingsStore.bassLayout : undefined,
            );
            movements = resp.movements ?? [];
            documentId = resp.document_id;
        } catch (error) {
            if (!isAbortError(error)) {
                notificationStore.notify(
                    extractApiErrorMessage(error, 'PDF-Konvertierung fehlgeschlagen'), 'error',
                );
            }
            return {upload: null, analyze: null};
        } finally {
            isUploading.value = false;
            // Die Objekt-URL wird nur für die PDF-Vorschau WÄHREND des OMR-Laufs gebraucht
            // (PreviewPane/UploadCard zeigen sie bei isUploading) – danach freigeben (Leak).
            if (currentPdfObjectUrl.value) {
                URL.revokeObjectURL(currentPdfObjectUrl.value);
                currentPdfObjectUrl.value = null;
            }
        }

        if (!movements.length) {
            notificationStore.notify('PDF-Konvertierung lieferte kein Ergebnis.', 'error');
            return {upload: null, analyze: null};
        }

        // Gemeinsame frische Ausgangslage (neues Dokument → altes Griffschrift-Ergebnis verwerfen).
        transposedSemitones.value = 0;
        currentEntryId.value = null;
        conversionResult.value = null;

        const first = movements[0]!;

        if (movements.length === 1) {
            // Einteilig: wie ein normaler frischer Upload (keine Tabs). In der History wird IMMER
            // das (teure) OMR-MusicXML gesichert – nicht die rohe PDF –, damit die
            // Wiederverwendung Audiveris überspringt.
            activeDocumentId.value = null;
            lineageId.value = newLineageId();
            fileInfo.value = toFileMeta(first);
            if (first.music_xml) selectedFileContent.value = first.music_xml;
            currentFileId.value = first.file_id;
            if (first.music_xml) {
                emitCapture({
                    kind: 'pdf-upload',
                    lineageId: lineageId.value,
                    filename: (first.filename || file.name).replace(/\.pdf$/i, '.musicxml'),
                    musicXml: first.music_xml,
                    fileType: first.file_type ?? '.musicxml',
                });
            }
            const analyzeResult = await analyze(first);
            return {upload: first, analyze: analyzeResult};
        }

        // Mehrteilig: pro Movement eine eigene Lineage; erstes Movement wird aktiv.
        const descriptors: PdfMovementDescriptor[] = movements.map((m, idx) => ({
            fileId: m.file_id,
            lineageId: newLineageId(),
            filename: m.filename,
            musicXml: m.music_xml ?? '',
            movementIndex: idx,
        }));

        activeDocumentId.value = documentId;
        lineageId.value = descriptors[0]!.lineageId;
        fileInfo.value = toFileMeta(first);
        if (first.music_xml) selectedFileContent.value = first.music_xml;
        currentFileId.value = first.file_id;

        // Alle Movements als EIN atomares Event erfassen (gemeinsame documentId, aktive Lineage).
        emitCapture({
            kind: 'document',
            documentId,
            activeLineageId: lineageId.value,
            movements: descriptors,
        });

        // Prominenter Hinweis-Dialog (statt nur einer Toast), damit die Tab-Leiste nicht
        // übersehen wird.
        multiPartNotice.value = descriptors.length;

        const analyzeResult = await analyze(first);
        return {upload: first, analyze: analyzeResult};
    }

    /** Schließt den Mehrteilig-Hinweis-Dialog. */
    function dismissMultiPartNotice() {
        multiPartNotice.value = null;
    }

    async function updateSelectedFileContent(musicXml: string) {
        if (!fileInfo.value?.filename) return;

        const file = new File([musicXml], fileInfo.value.filename, {
            type: 'application/vnd.recordare.musicxml+xml',
        });

        // Manueller Edit (Akkord/Note/Vorzeichen) → bleibt in derselben Bearbeitungslinie,
        // wird in der History als 'edited'-Variante erfasst (nicht als neues Original).
        if (!lineageId.value) lineageId.value = newLineageId();

        await uploadFile(file, {intent: 'edit'});
    }

    // ============ Analysis Actions ============

    async function refreshTranspositionSuggestions(): Promise<TranspositionResult[]> {
        const requestedContent = selectedFileContent.value;
        const requestedFileId = currentFileId.value;
        if (!requestedContent || !requestedFileId) {
            resetTranspositionSuggestions();
            return [];
        }

        const generation = ++transpositionRequestGeneration;
        transpositionSuggestions.value = [];
        modeChangeSuggestions.value = [];
        transpositionSuggestionsError.value = null;
        isFindingTranspositions.value = true;
        try {
            const result = await apiClient.findTranspositionSuggestions(
                requestedFileId,
                requestedContent,
                {signal: beginRequest('transposition-suggestions')},
            );
            if (
                generation !== transpositionRequestGeneration ||
                selectedFileContent.value !== requestedContent
            ) return [];
            transpositionSuggestions.value = [...(result.suggestions ?? [])];//sort((a, b) => a.analysis.unmappable_count - b.analysis.unmappable_count);
            modeChangeSuggestions.value = [...(result.mode_change_suggestions ?? [])];
            return transpositionSuggestions.value;
        } catch (error) {
            if (isAbortError(error)) return [];
            if (
                generation === transpositionRequestGeneration &&
                selectedFileContent.value === requestedContent
            ) {
                transpositionSuggestionsError.value = extractApiErrorMessage(
                    error,
                    'Transpositionsvorschläge konnten nicht ermittelt werden',
                );
            }
            return [];
        } finally {
            if (
                generation === transpositionRequestGeneration &&
                selectedFileContent.value === requestedContent
            ) {
                isFindingTranspositions.value = false;
            }
        }
    }

    /**
     * Übernimmt ein bereits vorliegendes Analyse-Ergebnis in den Store (setzt `analysisResult`,
     * aktualisiert die Part-Auswahl, verbucht optional das History-`analysis`-Event). Anders als
     * {@link analyze} wird `analysisResult` **nicht** vorab auf null gesetzt und **kein**
     * abbrechbarer Request gestartet – das Ergebnis ist atomar. Genutzt für die von den
     * Transform-Endpunkten (transpose/merge-voices) **inline mitgelieferte** Analyse, damit die
     * Anzeige nach der Operation nicht durch den fragilen separaten `analyzeSelected()`-Aufruf
     * (null→abort) leer bleibt.
     */
    function applyAnalysisResult(
        result: AnalysisResult,
        opts: { capture?: boolean } = {},
    ): void {
        if (result.parts?.length) {
            analysisResult.value = result;
            useSettingsStore().updateSettings({
                selected_parts: result.parts.map((part) => part.index),
            });
        }

        if ((opts.capture ?? true) && lineageId.value) {
            emitCapture({
                kind: 'analysis',
                lineageId: lineageId.value,
                semitones: transposedSemitones.value,
                analysis: result,
            });
        }
        void refreshTranspositionSuggestions();
    }

    /**
     * Übernimmt die inline-Analyse eines Transform-Endpunkts; fehlt sie (altes Backend ohne
     * mitgeliefertes `analysis`), re-analysiert `analyzeSelected`. **Konvertiert bewusst NICHT** –
     * die Griffschrift entsteht ausschließlich auf Klick, eine bereits vorhandene wird über
     * {@link isConversionStale} als veraltet markiert. Muss NACH dem Zurücksetzen des jeweiligen
     * Lade-Flags laufen, weil `analyze` seinen eigenen `isPending`-Guard hat.
     */
    async function applyTransformAnalysis(analysis: AnalysisResult | null | undefined): Promise<void> {
        if (analysis) {
            applyAnalysisResult(analysis);
        } else {
            await analyzeSelected();
        }
    }

    /**
     * Gemeinsamer Rahmen der Transform-Actions (Transpose, Part-Transpose, Shift, Merge,
     * Bass-System): Guards (Datei vorhanden, keine laufende Operation, optional Inhalt
     * vorhanden), Loading-Flag setzen/zurücksetzen, Fehler-Toast (außer bei Abbruch). `fn`
     * macht den eigentlichen Request + Commit und läuft innerhalb des Flags; die inline
     * mitgelieferte Analyse wendet der Rahmen **nach** dem Flag-Reset an (sonst griffe der
     * `isPending`-Guard der Folge-Analyse). Bewusst NICHT für `applyChordInference`
     * (kein isPending-Guard, stiller Rückzug – eigener Pfad).
     */
    async function runTransform(opts: {
        flag: Ref<boolean>;
        failure: string;
        /** Gesetzt → Content-Guard aktiv, mit genau diesem Hinweistext. */
        missingContentMessage?: string;
        fn: (fileId: string, content: string) => Promise<FileUploadResponse | null>;
    }): Promise<FileUploadResponse | null> {
        const fid = currentFileId.value;
        if (!fid) {
            notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
            return null;
        }
        if (isPending.value) {
            notificationStore.notify(
                'Eine Analyse oder Konvertierung läuft bereits. Bitte warten.',
                'warning'
            );
            return null;
        }
        const content = selectedFileContent.value;
        if (opts.missingContentMessage && !content) {
            notificationStore.notify(opts.missingContentMessage, 'warning', 2000);
            return null;
        }

        opts.flag.value = true;
        let res: FileUploadResponse | null = null;
        try {
            res = await opts.fn(fid, content ?? '');
        } catch (error) {
            if (!isAbortError(error)) {
                notificationStore.notify(extractApiErrorMessage(error, opts.failure), 'error');
            }
            return null;
        } finally {
            opts.flag.value = false;
        }
        if (!res) return null;

        // Analyse kommt inline aus der Transform-Response (atomar; kein separater, abbrechbarer
        // /api/analyze-Request → keine leere/veraltete Anzeige durch null→abort). Fallback für
        // alte Backends ohne mitgeliefertes `analysis`.
        await applyTransformAnalysis(res.analysis);
        return res;
    }

    /**
     * Der gemeinsame „Commit"-Schritt der einschrittigen Transform-Actions: macht das (bereits
     * finale, serverseitig normalisierte) Ergebnis zur neuen Arbeitsbasis und verbucht es als
     * Capture-Event im Verlauf (Capture-Queue → addDerived setzt den currentEntryId-Undo-Zeiger,
     * die Undo-Kette bleibt intakt). `resetSemitones` für Operationen, deren Ergebnis-Bytes die
     * Transposition bereits enthalten (Shift/Merge – analog loadFromHistory).
     */
    function commitWorkingContent(
        res: FileUploadResponse,
        musicXml: string,
        opts: {
            kind: 'edited' | 'shifted' | 'merged' | 'mode-changed';
            fallbackFilename: string;
            resetSemitones?: boolean;
        },
    ): void {
        if (!lineageId.value) lineageId.value = newLineageId();
        if (opts.resetSemitones) transposedSemitones.value = 0;
        selectedFileContent.value = musicXml;
        emitCapture({
            kind: opts.kind,
            lineageId: lineageId.value,
            filename: res.filename || fileInfo.value?.filename || opts.fallbackFilename,
            musicXml,
            semitones: transposedSemitones.value,
            fileType: res.file_type ?? null,
        });
    }

    async function analyze(
        file: FileUploadResponse,
        opts: {
            /** false = reine UI-Re-Analyse (z. B. Restore) – nicht in der History verbuchen. */
            capture?: boolean;
        } = {},
    ): Promise<AnalysisResult | null> {
        if (isPending.value) {
            notificationStore.notify(
                'Eine Analyse oder Konvertierung läuft bereits. Bitte warten.',
                'warning'
            );
            return null;
        }

        fileInfo.value = toFileMeta(file);
        isAnalyzing.value = true;
        analysisResult.value = null;
        resetTranspositionSuggestions();

        try {
            // Inhaltsbasiert (stateless): den aktuell angezeigten Inhalt analysieren.
            const content = selectedFileContent.value ?? file.music_xml ?? '';
            const result = await apiClient.analyzeFile(file.file_id, content, {
                signal: beginRequest('analyze'),
            });

            applyAnalysisResult(result, opts);

            if (file.filename) {
                notificationStore.notify(
                    `Datei "${file.filename}" analysiert.`,
                    'success',
                    2000
                );
            }

            return result;

        } catch (error) {
            if (isAbortError(error)) return null;
            notificationStore.notify(extractApiErrorMessage(error, 'Analyse fehlgeschlagen'), 'error');
            return null;
        } finally {
            isAnalyzing.value = false;

        }
    }

    /** Re-analysiert den aktuellen Arbeitsinhalt. **Konvertiert bewusst NICHT** – siehe
     *  {@link isConversionStale}. */
    async function analyzeSelected(opts: { capture?: boolean } = {}): Promise<AnalysisResult | null> {
        if (!fileInfo.value) return null;
        return analyze(fileInfo.value, opts);
    }

    // ============ Transposition Actions ============

    function setCurrentFileId(id: string) {
        currentFileId.value = id;
    }

    function setTransposedSemitones(value: number) {
        transposedSemitones.value = value;
    }

    // Erlaubt der Dateihistorie, beim Erkennen eines inhaltsgleichen Uploads die bestehende
    // Bearbeitungslinie zu übernehmen (Duplikat-Vermeidung via Content-Hash).
    function setLineageId(id: string) {
        lineageId.value = id;
    }

    function setCurrentEntryId(id: string | null) {
        currentEntryId.value = id;
    }

    // Lädt einen gespeicherten Verlaufsstand (Bytes) als aktuelle Arbeitsbasis – OHNE die
    // Bearbeitungslinie zu wechseln (anders als reuse(), das eine neue Linie startet) und ohne
    // einen neuen Verlaufseintrag zu erzeugen. Genutzt von der "Schritt zurück"-Funktion
    // (useFileHistoryStore.loadPreviousState). intent='restore' erzeugt bewusst kein
    // Capture-Event; der currentEntryId-Zeiger wird hier explizit auf den geladenen Eintrag
    // gesetzt. Die anschließende Analyse ist eine reine UI-Re-Analyse (capture: false).
    async function loadFromHistory(
        file: File,
        opts: { entryId: string },
    ): Promise<boolean> {
        if (isPending.value) {
            notificationStore.notify('Bitte warten – es läuft bereits eine Operation.', 'warning', 2000);
            return false;
        }
        // Die geladenen Bytes sind der fertige Stand (Transposition/Verschiebung bereits enthalten)
        // → neue Arbeitsbasis mit Transposition 0 (analog applyDiatonicShift). So wird der Stand
        // beim Reload (initFromUrl) NICHT erneut transponiert.
        transposedSemitones.value = 0;
        const upload = await uploadFile(file, {silent: true, intent: 'restore'});
        if (!upload) return false;
        setCurrentEntryId(opts.entryId);
        await analyzeSelected({capture: false});
        return true;
    }

    async function transposeScore(
        fileIdParam: string,
        semitones: number
    ): Promise<FileUploadResponse> {
        // Inhaltsbasiert: den aktuell angezeigten Inhalt um das Delta `semitones` transponieren.
        // (Vorschläge/Manuelle Eingabe sind stets ein Delta relativ zum aktuellen Stand.)
        // Die kumulierte Transposition wird weiterhin für Anzeige/History/Convert mitgeführt.
        const baseContent = selectedFileContent.value ?? '';

        const data = await apiClient.transposeFile(fileIdParam, semitones, baseContent, undefined, {
            signal: beginRequest('transpose'),
        });

        transposedSemitones.value += semitones;
        selectedFileContent.value = data.music_xml ?? null;

        if (data.music_xml && lineageId.value) {
            emitCapture({
                kind: 'transposed',
                lineageId: lineageId.value,
                filename: data.filename || 'transponiert.musicxml',
                musicXml: data.music_xml,
                semitones: transposedSemitones.value,
                fileType: data.file_type ?? null,
            });
        }

        return data;
    }

    async function applyTransposition(
        suggestion: TranspositionResult
    ): Promise<FileUploadResponse | null> {
        if (!currentFileId.value || !suggestion || suggestion.semitones === 0) {
            notificationStore.notify('Keine Transposition angewendet.', 'info');
            return null;
        }
        return runTransform({
            flag: isTransposing,
            failure: 'Transposition fehlgeschlagen',
            fn: (fid) => transposeScore(fid, suggestion.semitones),
        });
    }

    /**
     * Transponiert **nur** die adressierten Parts (0-basierte Indizes in `score.parts`) um
     * `semitones`. Anders als {@link transposeScore} bleibt der kumulative Ganz-Score-Zähler
     * `transposedSemitones` **unverändert** (Per-Part-Transpose ist eine inhaltliche Korrektur,
     * kein globaler Transpositions-Schritt) und das Ergebnis wird als `edited`-Verlaufseintrag
     * erfasst (Lineage erhalten).
     */
    async function applyPartTransposition(
        partIndices: number[],
        semitones: number
    ): Promise<FileUploadResponse | null> {
        if (!currentFileId.value || !partIndices.length || semitones === 0) {
            notificationStore.notify('Keine Transposition angewendet.', 'info');
            return null;
        }
        return runTransform({
            flag: isTransposing,
            failure: 'Transposition fehlgeschlagen',
            fn: async (fid, content) => {
                const data = await apiClient.transposeFile(fid, semitones, content, partIndices, {
                    signal: beginRequest('transpose'),
                });
                // Kein transposedSemitones-Bump: Per-Part-Transpose ist eine Inhalts-Korrektur.
                if (data.music_xml) {
                    commitWorkingContent(data, data.music_xml, {
                        kind: 'edited', fallbackFilename: 'teil-transponiert.musicxml',
                    });
                }
                return data;
            },
        });
    }

    /**
     * Wendet einen Variantwechsel-Vorschlag (Dur↔Moll, `mode_change='parallel'`) an: der Server
     * remappt die Stufen in die Varianttonart und transponiert danach um `suggestion.semitones`.
     * Die Ergebnis-Bytes sind final (inkl. des chromatischen Anteils) → neue Arbeitsbasis mit
     * Transposition 0 und **kein** `transposedSemitones`-Bump (analog Shift/Merge).
     */
    async function applyModeChange(
        suggestion: TranspositionResult
    ): Promise<FileUploadResponse | null> {
        const targetMode = suggestion.target_mode;
        if (!currentFileId.value || (targetMode !== 'major' && targetMode !== 'minor')) {
            notificationStore.notify('Kein Variantwechsel angewendet.', 'info');
            return null;
        }
        return runTransform({
            flag: isTransposing,
            failure: 'Variantwechsel fehlgeschlagen',
            missingContentMessage: 'Kein Inhalt für den Variantwechsel vorhanden.',
            fn: async (fid, content) => {
                const res = await apiClient.modeChange(fid, content, targetMode, suggestion.semitones, {
                    signal: beginRequest('mode-change'),
                });
                if (!res?.music_xml) {
                    notificationStore.notify('Variantwechsel fehlgeschlagen.', 'error');
                    return null;
                }
                commitWorkingContent(res, res.music_xml, {
                    kind: 'mode-changed', fallbackFilename: 'variantwechsel.musicxml', resetSemitones: true,
                });
                return res;
            },
        });
    }

    // ============ Diatonic Shift Actions ============

    /**
     * Wendet eine diatonische Verschiebung (Terz hinzufügen / Dreiklang reduzieren) auf den
     * aktuellen Upload an und macht das Ergebnis zur **neuen Arbeitsbasis**. **Einschrittig**
     * (kein Re-Upload): Der Endpunkt liefert den bereits normalisierten Inhalt **und** die Analyse
     * inline; das Ergebnis wird direkt gesetzt (analog applyPartTransposition), sodass es
     * anschließend weiter transponiert/konvertiert werden kann (z. B. erst Shift, dann
     * Transponieren). Die Bearbeitungslinie (lineageId) bleibt erhalten, die Transposition startet
     * bei 0; das direkt emittierte 'shifted'-Capture-Event verbucht den Stand im Verlauf.
     */
    async function applyDiatonicShift(
        settings: DiatonicShiftSettings
    ): Promise<FileUploadResponse | null> {
        return runTransform({
            flag: isShifting,
            failure: 'Diatonische Verschiebung fehlgeschlagen',
            missingContentMessage: 'Kein Inhalt zum Verschieben vorhanden.',
            fn: async (fid, content) => {
                // Inhaltsbasiert: aktuell angezeigtes MusicXML senden (reihenfolgeunabhängig korrekt).
                const res = await apiClient.shiftFile(fid, settings, content, {
                    signal: beginRequest('shift'),
                });
                if (!res?.music_xml) {
                    notificationStore.notify('Diatonische Verschiebung fehlgeschlagen.', 'error');
                    return null;
                }
                shiftResponse.value = res;
                commitWorkingContent(res, res.music_xml, {
                    kind: 'shifted', fallbackFilename: 'verschoben.musicxml', resetSemitones: true,
                });
                return res;
            },
        });
    }

    // ============ Voice Merge Actions ============

    /**
     * Führt die Stimmen/Parts des aktuellen Uploads zusammen (ehemals COMBINE) und macht das
     * Ergebnis zur **neuen Arbeitsbasis**. **Einschrittig** (kein Re-Upload): Der Endpunkt liefert
     * den bereits normalisierten Inhalt **und** die Analyse inline; das Ergebnis wird direkt gesetzt
     * (analog applyPartTransposition), sodass es anschließend weiter transponiert/verschoben/
     * konvertiert werden kann. Die Bearbeitungslinie (lineageId) bleibt erhalten, die Transposition
     * startet bei 0; das direkt emittierte 'merged'-Capture-Event verbucht den Stand im Verlauf.
     * Inhaltsbasiert/reihenfolgeunabhängig (analog Shift).
     */
    async function applyVoiceMerge(
        settings: VoiceMergeSettings
    ): Promise<FileUploadResponse | null> {
        return runTransform({
            flag: isMerging,
            failure: 'Stimmen-Zusammenführen fehlgeschlagen',
            missingContentMessage: 'Kein Inhalt zum Zusammenführen vorhanden.',
            fn: async (fid, content) => {
                // Inhaltsbasiert: aktuell angezeigtes MusicXML senden (reihenfolgeunabhängig korrekt).
                const res = await apiClient.mergeVoices(fid, settings, content, {
                    signal: beginRequest('merge'),
                });
                if (!res?.music_xml) {
                    notificationStore.notify('Stimmen-Zusammenführen fehlgeschlagen.', 'error');
                    return null;
                }
                mergeResponse.value = res;
                commitWorkingContent(res, res.music_xml, {
                    kind: 'merged', fallbackFilename: 'zusammengefuehrt.musicxml', resetSemitones: true,
                });
                return res;
            },
        });
    }

    // ============ Bass Preview Actions ============

    /**
     * Erzeugt (oder entfernt) das klingende Bass-System im Bassschlüssel.
     *
     * Der Part zeigt vor der Konvertierung, welchen Wechselbass die Harmonika zu den erkannten
     * Akkorden spielen würde – die Prüfhilfe für die Akkorderkennung. Er ist **reine Anzeige**:
     * Analyse und Konvertierung klammern ihn serverseitig aus, ein eigener Basslauf wirkt also
     * nicht auf die Griffschrift-Ausgabe. Wirkung hat nur, was der Nutzer an den AKKORDEN ändert.
     *
     * Die Bass-Belegung kommt aus dem Harmonika-Tab (``settingsStore.bassLayout``).
     */
    /**
     * Setzt die Spielweise eines Takts im Bass-System und baut es neu auf.
     *
     * Der aktuelle Stand wird aus den Score-Metadaten gelesen, ergänzt und vollständig
     * mitgeschickt – geschrieben wird ausschließlich serverseitig, damit es einen Schreibpfad gibt.
     * `wechselbass` ist die Vorgabe und wird als Eintrag wieder entfernt.
     */
    async function setBassMeasureStyle(
        measureIndex: number, style: string,
    ): Promise<FileUploadResponse | null> {
        const current = readBassPreviewStyles(selectedFileContent.value ?? '');
        const next: Record<string, string> = {};
        for (const [index, value] of Object.entries(current)) next[index] = value;
        if (style && style !== 'wechselbass') next[String(measureIndex)] = style;
        else delete next[String(measureIndex)];

        return applyBassPreview(false, next);
    }

    async function applyBassPreview(
        remove = false, measureStyles?: Record<string, string>,
    ): Promise<FileUploadResponse | null> {
        const failure = remove ? 'Bass-System entfernen fehlgeschlagen' : 'Bass-System erzeugen fehlgeschlagen';
        return runTransform({
            flag: isBuildingBassPreview,
            failure,
            missingContentMessage: 'Kein Inhalt für das Bass-System vorhanden.',
            fn: async (fid, content) => {
                const res = await apiClient.bassPart(
                    fid,
                    settingsStore.bassLayout,
                    content,
                    remove,
                    measureStyles,
                    {signal: beginRequest('bass-preview')},
                );
                if (!res?.music_xml) {
                    notificationStore.notify(`${failure}.`, 'error');
                    return null;
                }
                // Einschrittig wie applyVoiceMerge: das Ergebnis ist final, KEIN Re-Upload
                // (process_upload würde die Akkorderkennung mit Server-Defaults erneut anwerfen).
                // Verlaufseintrag als 'edited': der Schritt ist über „Zurücksetzen" rücknehmbar,
                // verdient aber keine eigene Verlaufs-Kategorie (er ist reine Anzeige).
                commitWorkingContent(res, res.music_xml, {
                    kind: 'edited', fallbackFilename: 'bass.musicxml',
                });
                return res;
            },
        });
    }

    // ============ Chord Inference Actions ============

    /**
     * Wendet die Akkorderkennung (per Button ausgelöst) auf den aktuellen Inhalt an. Der Server
     * entfernt zuvor automatisch erkannte (markierte) Akkorde und erkennt mit den übergebenen
     * Settings neu; originale Symbole bleiben erhalten – außer ``settings.overwrite_existing`` ist
     * gesetzt, dann werden auch diese ersetzt. Das (bereits finale) Ergebnis wird **direkt** als
     * aktuelle Arbeitsbasis (``selectedFileContent``) gesetzt (kein Re-Upload, kein
     * Verlaufseintrag). Keine Re-Analyse nötig (Erkennung ändert Tonart/Spielbarkeit nicht).
     * Überlappende Aufrufe werden per ``beginRequest('chord-inference')`` abgebrochen.
     */
    async function applyChordInference(
        settings: ChordInferenceSettings,
    ): Promise<FileUploadResponse | null> {
        const fid = currentFileId.value;
        const content = selectedFileContent.value;
        if (!fid || !content) return null;
        isDetectingChords.value = true;
        let res: FileUploadResponse | null = null;
        try {
            res = await apiClient.inferChordSymbols(
                fid, settings, content, {
                    signal: beginRequest('chord-inference'),
                },
                settingsStore.bassLayout,
            );
            if (!res?.music_xml) return null;

            // WICHTIG: Ergebnis ist bereits final (der Endpunkt entfernt zuvor Erkanntes und erkennt
            // mit den User-Settings neu). Direkt als aktuellen Inhalt setzen – NICHT über
            // uploadFile/POST /api/upload: process_upload würde die Akkorderkennung dort erneut mit
            // den SERVER-Default-Settings ausführen und so die User-Auswahl (z. B. strict/aus)
            // überschreiben (der Guard greift nur bei symbollosem Score). Kein Verlaufseintrag –
            // Live-Änderungen ersetzen den aktuellen Stand. Die Vorschau re-rendert über den
            // selectedFileContent-Watcher; fileId bleibt gültig (stateless, opakes Label).
            selectedFileContent.value = res.music_xml;
        } catch (error) {
            if (!isAbortError(error)) {
                notificationStore.notify(
                    extractApiErrorMessage(error, 'Akkorderkennung fehlgeschlagen'), 'error',
                );
            }
            return null;
        } finally {
            isDetectingChords.value = false;
        }
        return res;
    }

    /**
     * Tauscht den Griff EINER Note der Griffschrift-**AUSGABE** gegen eine Alternative –
     * **client-seitig** (keine Server-Runde). Der Griff kodiert seine Anzeige selbst
     * (`griffschrift_pitch`/`notehead`/`color`/Balgrichtung samt passender Bass-Silbe), daher schreibt
     * {@link applyGripToNoteInMusicXml} die Note direkt im MusicXML um (dauererhaltend →
     * keine `MeterException`). OUTPUT-Edit: aktualisiert NUR `conversionResult` (nicht
     * `selectedFileContent` – das wäre die Input-Lineage), lean/ohne History-Capture. Vorschau +
     * alle Downloads lesen `previewFileContent` (= `conversionResult.music_xml`) und re-rendern
     * automatisch. Fehler/Adress-Mismatch → Toast.
     */
    function applyGripToConvertedNote(noteInfo: NoteInfo, grip: Grip): void {
        const current = conversionResult.value;
        if (!current?.music_xml || !noteInfo || !grip?.buttons?.length) return;
        const updated = applyGripToNoteInMusicXml(
            current.music_xml, noteInfo, grip.buttons, grip.is_druck, grip.bass_lyric,
        );
        if (!updated) {
            notificationStore.notify('Griff konnte nicht getauscht werden.', 'error');
            return;
        }
        conversionResult.value = {...current, music_xml: updated};
    }

    /**
     * Setzt oder löscht (`text` = `null`/leer) die **Bass-Notation** (Bass-Lyric `number="3"`/`name="Bass"`)
     * der ausgewählten Noten/Pausen/Träger der Griffschrift-**AUSGABE** – **client-seitig** via
     * {@link setBassLyricInMusicXml} (nur `<lyric>` ändert sich, nie `<duration>`). OUTPUT-Edit wie
     * {@link applyGripToConvertedNote}: aktualisiert NUR `conversionResult` (nicht `selectedFileContent`),
     * ohne History-Capture; Vorschau + Downloads lesen `previewFileContent`. Eine Neukonvertierung erzeugt
     * die Bass-Zeile neu. Keine Note auflösbar (z. B. Alt-Inhalte mit nicht-numerischen Voice-IDs) → Toast.
     */
    function applyBassLyricToConvertedNotes(notes: readonly NoteInfo[], text: string | null): void {
        const current = conversionResult.value;
        if (!current?.music_xml || !notes.length) return;
        const updated = setBassLyricInMusicXml(current.music_xml, notes, text);
        if (!updated) {
            notificationStore.notify('Bass-Notation konnte nicht geändert werden.', 'error');
            return;
        }
        conversionResult.value = {...current, music_xml: updated};
    }

    /**
     * Übernimmt das Ergebnis einer **Gesangstext-Sitzung** auf der EINGABE-Vorschau als neue
     * Arbeitsbasis – **client-seitig, bewusst OHNE `POST /api/upload` (kritisch – nicht auf
     * `updateSelectedFileContent` rückbauen)**: music21 kennt `<extend/>` in KEINER Richtung
     * (literal `# TODO: extend` in `m21ToXml.lyricToXml` UND `xmlToM21.xmlToLyric`) und verwirft
     * text-lose `<lyric>` ganz – ein Re-Upload löschte also **jedes Melisma** der Datei, nicht nur
     * die neu getippten. Der Schritt ist beweisbar strukturneutral (nur `<lyric>` ändert sich, nie
     * `<duration>`), `normalize_part_voices` hätte daher nichts zu tun, und die Analyse (Tonart,
     * Spielbarkeit) hängt nicht am Liedtext → auch kein `analyzeSelected()`.
     *
     * Verlaufseintrag `edited` wie beim manuellen Edit (Capture-Queue → `addEdited` → `addDerived`
     * setzt den `currentEntryId`-Undo-Zeiger), Lineage bleibt erhalten. Analog zum Direkt-Pfad von
     * {@link applyDiatonicShift}/{@link applyVoiceMerge}.
     */
    function applyLyricsToSelectedFileContent(musicXml: string): void {
        if (!musicXml || musicXml === selectedFileContent.value) return;
        if (!lineageId.value) lineageId.value = newLineageId();
        selectedFileContent.value = musicXml;
        emitCapture({
            kind: 'edited',
            lineageId: lineageId.value,
            filename: fileInfo.value?.filename || 'bearbeitet.musicxml',
            musicXml,
            semitones: transposedSemitones.value,
            fileType: fileInfo.value?.file_type ?? null,
        });
    }

    /**
     * Übernimmt eine **Gesangstext-Sitzung** der Griffschrift-**AUSGABE**: OUTPUT-Edit wie
     * {@link applyGripToConvertedNote}/{@link applyBassLyricToConvertedNotes} – nur
     * `conversionResult` (→ `previewFileContent`), **kein** History-Capture, **kein** Backend. Eine
     * Neukonvertierung überschreibt das Ergebnis erwartungsgemäß.
     */
    /** Ersetzt das XML der Griffschrift-AUSGABE (`conversionResult.music_xml`) – der gemeinsame
     *  Rückschreibpfad für **alle** Ausgabe-Edits: Gesangstext-Sitzung, und über
     *  `PreviewPane.saveConvertedUpdate` auch die per Kurzbefehl ausgelösten Edits
     *  (Transponieren, Diatonisch, Noten-Edits). Bewusst **kein** Upload und **kein**
     *  Verlaufseintrag – analog Griff/Bass: die Ausgabe ist ein Derivat und wird bei der nächsten
     *  Konvertierung ohnehin überschrieben. */
    function applyEditedConvertedXml(musicXml: string): void {
        const current = conversionResult.value;
        if (!current?.music_xml || !musicXml) return;
        conversionResult.value = {...current, music_xml: musicXml};
    }

    // ============ Conversion Actions ============

    async function startConversion(
        settings?: ConversionSettings
    ): Promise<string | null> {
        if (isPending.value) {
            notificationStore.notify(
                'Eine Konvertierung läuft bereits. Bitte warten.',
                'warning'
            );
            return null;
        }

        const fileIdLocal = currentFileId.value;
        if (!fileIdLocal) {
            notificationStore.notify(
                'Bitte zunächst eine Datei hochladen.',
                'warning'
            );
            return null;
        }

        // Pin: eine Konvertierung überschreibt nie Original-Akkorde (overwrite_existing=false) –
        // das Backend honoriert alle gesendeten Werte, der Schutz ist Frontend-Sache.
        const effectiveSettings = pinConversionSettings(settings ?? settingsStore.settings);
        const mode = settingsStore.mode as ConversionMode;

        isConverting.value = true;

        // Genau EINMAL lesen: derselbe String geht an den Server und in den Stale-Merker – zwei
        // Lesezugriffe könnten bei einem Edit währenddessen auseinanderlaufen.
        const convertedContent = selectedFileContent.value ?? '';

        try {
            // Inhaltsbasiert: den aktuell angezeigten (bereits finalen) Inhalt konvertieren.
            const response = await apiClient.convertFile({
                fileId: fileIdLocal,
                musicXml: convertedContent,
                mode,
                settings: {...effectiveSettings} as ConversionSettings,
                // Titel des Ergebnis-Scores. Wurde serverseitig immer unterstützt
                // (_resolve_title), aber vom Client nie gesendet -> Titel war stets
                // 'Griffschrift'.
                originalFilename: fileInfo.value?.filename ?? undefined,
            }, {signal: beginRequest('convert')});

            if (!response) {
                notificationStore.notify(
                    'Konvertierung fehlgeschlagen: Keine Antwort vom Server.',
                    'error'
                );
                return null;
            }

            setConversionResult(response, effectiveSettings as ConversionSettings, convertedContent);
            notificationStore.notify('Konvertierung abgeschlossen.', 'success', 2000);

            return response.music_xml ?? null;

        } catch (error) {
            if (isAbortError(error)) return null;
            notificationStore.notify(
                extractApiErrorMessage(error, 'Konvertierung fehlgeschlagen'), 'error',
            );
            return null;
        } finally {
            isConverting.value = false;
            // KEIN Tab-Switch hier: einziger Owner des Auto-Wechsels ist der
            // `isConverting`-Watcher in useTabStore (der frühere finally-Switch lief auch bei
            // Fehlschlag/Abbruch und wechselte dann auf eine leere/veraltete Ausgabe).
        }
    }

    // Zentrale Ergebnis-Übernahme (auch für andere Stores, z. B. Per-Measure-Assemble): setzt das
    // Ergebnis in die Preview-/Download-Pipeline UND verbucht es als 'converted'-Capture-Event.
    // `usedSettings` = die tatsächlich an den Server geschickten Settings (Per-Measure friert sie
    // ein); das Event trägt einen JSON-Snapshot zum Emit-Zeitpunkt – der Konsument
    // (useHistoryCapture) liest NICHT mehr live aus dem Settings-Store.
    // `sourceMusicXml` = der Eingabe-Inhalt, aus dem dieses Ergebnis entstand (Per-Measure reicht
    // seinen eingefrorenen Stand durch). Er speist `isConversionStale`; ein `null`-Ergebnis leert
    // ihn mit, sodass z. B. der Movement-Wechsel nichts extra aufräumen muss.
    function setConversionResult(
        result: ConversionStatusStore | null,
        usedSettings?: ConversionSettings,
        sourceMusicXml?: string | null,
    ) {
        conversionResult.value = result;
        lastConvertedContent.value = result ? (sourceMusicXml ?? null) : null;
        if (result?.music_xml && lineageId.value) {
            emitCapture({
                kind: 'converted',
                lineageId: lineageId.value,
                baseFilename: fileInfo.value?.filename ?? 'griffschrift.musicxml',
                musicXml: result.music_xml,
                conversionId: result.conversion_id ?? null,
                semitones: transposedSemitones.value,
                analysis: analysisResult.value,
                settings: JSON.parse(JSON.stringify(usedSettings ?? settingsStore.settings)) as ConversionSettings,
            });
        }
    }


    // ============ Reset ============

    function reset() {
        // Laufende Requests abbrechen (deren Antworten sind nach dem Reset bedeutungslos).
        requests.abortAll();
        transpositionRequestGeneration += 1;

        // Reset loading states
        isUploading.value = false;
        isAnalyzing.value = false;
        isConverting.value = false;
        isTransposing.value = false;
        isShifting.value = false;
        isMerging.value = false;
        isBuildingBassPreview.value = false;
        isDetectingChords.value = false;
        isFindingTranspositions.value = false;

        if (currentPdfObjectUrl.value) {
            URL.revokeObjectURL(currentPdfObjectUrl.value);
            currentPdfObjectUrl.value = null;
        }

        // Reset file state
        fileInfo.value = null;
        selectedFileContent.value = null;
        shiftResponse.value = null;
        mergeResponse.value = null;

        // Reset analysis & conversion
        analysisResult.value = null;
        transpositionSuggestions.value = [];
        modeChangeSuggestions.value = [];
        transpositionSuggestionsError.value = null;
        conversionResult.value = null;
        lastConvertedContent.value = null;

        // URL-State + Bearbeitungslinie leeren
        currentFileId.value = null;        // entfernt ?file aus der URL
        transposedSemitones.value = 0;     // entfernt ?semitones aus der URL
        lineageId.value = '';
        currentEntryId.value = null;     // entfernt ?entry aus der URL
        activeDocumentId.value = null;   // entfernt ?document aus der URL
        multiPartNotice.value = null;
        captureQueue.value = [];
    }

    // ============ Restore aus der URL beim Laden ============

    // Reiner Fallback (stateless): aus der `file_id` allein kann der Server NICHTS rekonstruieren –
    // der eigentliche Reload-Restore läuft clientseitig über die lokale History
    // (App.vue → useFileHistoryStore().restoreCurrentFromHistory()). Ist dort kein Stand vorhanden,
    // wird der nun nutzlose URL-Arbeitszustand verworfen. (Der Upload-Store importiert den
    // History-Store bewusst NICHT – daher orchestriert App.vue.)
    async function initFromUrl() {
        const fid = currentFileId.value;
        if (!fid || isPending.value) return;

        reset();
        notificationStore.notify(
            'Die zuletzt geöffnete Datei ist nicht im lokalen Verlauf. Bitte über den Verlauf erneut laden.',
            'info',
            4000,
        );
    }


    // ============ Public API ============

    return {
        // File state
        fileInfo,
        selectedFileContent,
        shiftResponse,
        mergeResponse,
        currentFileId,
        transposedSemitones,
        lineageId,
        currentEntryId,
        activeDocumentId,
        multiPartNotice,
        currentPdfObjectUrl,
        // History-Capture (Konsument: useHistoryCapture)
        captureQueue,
        drainCaptureQueue,
        // Loading states
        isUploading,
        isAnalyzing,
        isConverting,
        isTransposing,
        isShifting,
        isMerging,
        isBuildingBassPreview,
        isDetectingChords,
        isFindingTranspositions,


        // Computed
        isHavingFile,
        isHavingGriffschriftResult,
        isConversionStale,
        isInputNotMappable,
        isPending,
        isReadyToConvert,
        chordInferenceState,
        bassPreviewState,
        hasChordSymbols,
        // Analysis & Conversion results
        analysisResult,
        transpositionSuggestions,
        modeChangeSuggestions,
        transpositionSuggestionsError,
        conversionResult,
        previewFileContent: computed(() => conversionResult.value?.music_xml),

        // Actions
        uploadFile,
        updateAndAnalyzeFile,
        uploadAndAnalyzePdf,
        dismissMultiPartNotice,
        updateSelectedFileContent,
        analyze,
        analyzeSelected,
        refreshTranspositionSuggestions,
        applyTransposition,
        applyPartTransposition,
        applyModeChange,
        transposeScore,
        applyDiatonicShift,
        applyVoiceMerge,
        applyBassPreview,
        setBassMeasureStyle,
        applyChordInference,
        applyGripToConvertedNote,
        applyBassLyricToConvertedNotes,
        applyLyricsToSelectedFileContent,
        applyEditedConvertedXml,
        startConversion,
        setConversionResult,
        reset,
        setCurrentFileId,
        setTransposedSemitones,
        setLineageId,
        setCurrentEntryId,
        loadFromHistory,
        initFromUrl,
        takeReuseId,

    } as const;
});
